import { useEffect } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export function GoogleAnalytics() {
  const { preferences, hasConsented } = useCookieConsent();
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  useEffect(() => {
    // Only load GA if user has consented and accepted analytics cookies
    if (!hasConsented || !preferences.analytics || !measurementId) {
      // Remove GA if it was previously loaded and user revoked consent
      if (window.gtag && !preferences.analytics) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
      return;
    }

    // Check if GA is already loaded
    if (window.gtag) {
      // Update consent to granted
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
      return;
    }

    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('consent', 'default', {
        analytics_storage: 'granted'
      });
      gtag('config', '${measurementId}', {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure'
      });
    `;
    document.head.appendChild(script2);

    window.gtag = function() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    };

    return () => {
      // Cleanup if component unmounts
      script1.remove();
      script2.remove();
    };
  }, [preferences.analytics, hasConsented, measurementId]);

  return null;
}
